import React, { useEffect, useState } from 'react';
import { useKeycloak } from '@react-keycloak/web';
import TableSkeleton from 'components/common/skeletons/TableSkeleton';
import FinesTable from 'components/fines/FinesTable';
import useProcessCorrectedPlate from 'hooks/fines/useProcessCorrectedPlate';
import { useNavigate } from 'react-router-dom';
import { Bounce, toast } from 'react-toastify';
import { useFines } from 'context/FinesContext';
import { Fine } from 'utils/types';
import useOfficerNotifyFine from 'hooks/fines/useOfficerNotifyFine';
import useRecallFine from 'hooks/fines/useRecallFine';
import useGetSmsTemplates from 'hooks/sms/useGetSmsTemplates';
import { RotateCcw } from 'lucide-react';
import {
  addVisibleFines,
  isFineInSelection,
  removeVisibleFines,
} from './fineSelection';

const BulkRecallModal = ({
  selectedCount,
  reason,
  smsTemplateId,
  templates,
  isLoadingTemplates,
  isRecalling,
  onReasonChange,
  onTemplateChange,
  onCancel,
  onConfirm,
}: {
  selectedCount: number;
  reason: string;
  smsTemplateId: string;
  templates: {
    id: number;
    templateType: string;
    offence: { offenceLabel: string } | null;
  }[];
  isLoadingTemplates: boolean;
  isRecalling: boolean;
  onReasonChange: (value: string) => void;
  onTemplateChange: (value: string) => void;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const canSubmit =
    selectedCount > 0 && reason.trim().length > 0 && smsTemplateId.length > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label="Close bulk recall modal"
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
        disabled={isRecalling}
      />
      <div className="relative bg-white rounded-xl shadow-xl p-6 w-full max-w-2xl flex flex-col gap-4 max-h-[90vh] overflow-y-auto">
        <div>
          <h2 className="text-base font-semibold text-gray-800">
            Bulk Recall Fines
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            This will recall {selectedCount} selected fine
            {selectedCount === 1 ? '' : 's'}.
          </p>
        </div>

        <div>
          <label
            htmlFor="bulk-recall-reason"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            Recall reason
          </label>
          <textarea
            id="bulk-recall-reason"
            value={reason}
            onChange={(event) => onReasonChange(event.target.value)}
            disabled={isRecalling}
            rows={4}
            placeholder="Radar calibration error on Langata Rd 2026-06-03 12:30-14:00"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-orange-500 focus:border-orange-500 text-gray-700 disabled:opacity-50"
          />
        </div>

        <div>
          <label
            htmlFor="bulk-recall-template"
            className="block text-sm font-medium text-gray-700 mb-2"
          >
            SMS template
          </label>
          <select
            id="bulk-recall-template"
            value={smsTemplateId}
            onChange={(event) => onTemplateChange(event.target.value)}
            disabled={isRecalling || isLoadingTemplates}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:ring-orange-500 focus:border-orange-500 text-gray-700 disabled:opacity-50"
          >
            <option value="">
              {isLoadingTemplates
                ? 'Loading templates...'
                : 'Select an SMS template'}
            </option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.templateType}
                {template.offence ? ` - ${template.offence.offenceLabel}` : ''}
              </option>
            ))}
          </select>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={isRecalling}
            className="px-4 py-2 rounded-md text-sm font-medium text-gray-600 border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isRecalling || !canSubmit}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RotateCcw className="h-4 w-4" />
            {isRecalling ? 'Recalling...' : 'Recall Selected'}
          </button>
        </div>
      </div>
    </div>
  );
};

const Fines = () => {
  const navigate = useNavigate();
  const { keycloak } = useKeycloak();
  const [localFines, setLocalFines] = useState<Fine[]>([]);
  const [selectedFines, setSelectedFines] = useState<Fine[]>([]);
  const [showBulkRecallModal, setShowBulkRecallModal] = useState(false);
  const [bulkRecallReason, setBulkRecallReason] = useState('');
  const [bulkRecallTemplateId, setBulkRecallTemplateId] = useState('');
  const client =
    process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'api-service-client';
  const isNTSAUser =
    keycloak.tokenParsed?.resource_access?.[client]?.roles?.includes('ntsa');

  const {
    currentPage,
    pageSize,
    fines,
    totalPages,
    isLoading: finesLoading,
    goToPage,
    handleFilter,
    handlePageSizeChange, // 👈 new
    totalEntries,
    isCaseChecker,
    caseCheckerFinesView,
    setCaseCheckerFinesView,
  } = useFines();
  const canBulkRecall = isCaseChecker && caseCheckerFinesView === 'all';

  const { mutateAsync: processCorrectedPlateMutation, isPending } =
    useProcessCorrectedPlate();
  const { mutateAsync: notifyFineMutation, isPending: isNotifyPending } =
    useOfficerNotifyFine();
  const { mutateAsync: recallFineMutation, isPending: isRecallingFines } =
    useRecallFine();
  const { data: smsTemplates = [], isLoading: isLoadingSmsTemplates } =
    useGetSmsTemplates({
      enabled: showBulkRecallModal,
    });

  useEffect(() => {
    if (fines) {
      setLocalFines(
        isNTSAUser ? fines.filter((fine) => fine.smsStatus === 'S') : fines,
      );
    }
  }, [fines, isNTSAUser]);

  useEffect(() => {
    setSelectedFines([]);
  }, [fines, caseCheckerFinesView]);

  const resetBulkRecallForm = () => {
    setShowBulkRecallModal(false);
    setBulkRecallReason('');
    setBulkRecallTemplateId('');
  };

  const handleToggleFineSelection = (fine: Fine) => {
    setSelectedFines((prev) =>
      isFineInSelection(fine, prev)
        ? prev.filter((selectedFine) => selectedFine.id !== fine.id)
        : [...prev, fine],
    );
  };

  const handleToggleSelectAllFines = (visibleFines: Fine[]) => {
    const allVisibleSelected =
      visibleFines.length > 0 &&
      visibleFines.every((fine) => isFineInSelection(fine, selectedFines));

    setSelectedFines((prev) =>
      allVisibleSelected
        ? removeVisibleFines(prev, visibleFines)
        : addVisibleFines(prev, visibleFines),
    );
  };

  const handleBulkRecall = async () => {
    const ticketNumbers = selectedFines.map((fine) => fine.ticketNumber);
    const reason = bulkRecallReason.trim();

    if (!ticketNumbers.length || !reason || !bulkRecallTemplateId) return;

    await recallFineMutation({
      ticketNumbers,
      reason,
      smsTemplateId: Number(bulkRecallTemplateId),
    })
      .then((response) => {
        toast.success(response.message, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        });
        resetBulkRecallForm();
        setSelectedFines([]);
      })
      .catch(() =>
        toast.error('There was an error recalling the selected fines', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        }),
      );
  };

  const handleRowUpdate = async (updatedRow: Fine) => {
    setLocalFines((prevFines) =>
      prevFines.map((fine) => (fine.id === updatedRow.id ? updatedRow : fine)),
    );
    const post = {
      ticketNumber: updatedRow.id.toString(),
      vehicleRegistrationNumber: updatedRow.vehicleRegistrationNumber,
      offences: [],
      userId: keycloak?.tokenParsed.sub,
    };
    await processCorrectedPlateMutation(post)
      .then(() =>
        toast.success('The registration was updated successfully', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          transition: Bounce,
        }),
      )
      .catch(() =>
        toast.error('There was an error updating the registration number', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          transition: Bounce,
        }),
      );
  };

  const handleRowClick = (fine: Fine) => {
    const currentIndex = localFines.findIndex((f) => f.id === fine.id);
    navigate(`/fines/single/${fine.ticketNumber}`, {
      state: {
        currentIndex,
        totalPages,
        currentPage,
      },
    });
  };

  const handleSend = async (fine: Fine) => {
    await notifyFineMutation({ fineId: fine.id.toString() })
      .then(() =>
        toast.success('Fine notification sent successfully', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        }),
      )
      .catch(() =>
        toast.error('There was an error sending the fine notification', {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          theme: 'light',
          transition: Bounce,
        }),
      );
  };

  return (
    <div
      className={`bg-white rounded-lg shadow p-6 relative
        ${isNTSAUser ? 'courier' : ''}`}
    >
      {showBulkRecallModal && (
        <BulkRecallModal
          selectedCount={selectedFines.length}
          reason={bulkRecallReason}
          smsTemplateId={bulkRecallTemplateId}
          templates={smsTemplates}
          isLoadingTemplates={isLoadingSmsTemplates}
          isRecalling={isRecallingFines}
          onReasonChange={setBulkRecallReason}
          onTemplateChange={setBulkRecallTemplateId}
          onCancel={resetBulkRecallForm}
          onConfirm={handleBulkRecall}
        />
      )}
      {isNTSAUser && (
        <div className="absolute inset-0 z-10 grid grid-rows-10 grid-cols-5 gap-4 opacity-25 pointer-events-none">
          {Array.from({ length: 50 }).map((_, idx) => (
            <div key={idx} className="flex items-center justify-center">
              <img
                src="/ntsa-logo.svg"
                alt="ntsa logo"
                className="w-24 h-24 object-contain"
              />
            </div>
          ))}
        </div>
      )}
      <div className="relative z-0">
        {isCaseChecker && (
          <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="inline-flex rounded-lg border border-gray-200 bg-gray-50 p-1">
              <button
                type="button"
                onClick={() => setCaseCheckerFinesView('pending')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  caseCheckerFinesView === 'pending'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Pending Case Checks
              </button>
              <button
                type="button"
                onClick={() => setCaseCheckerFinesView('all')}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                  caseCheckerFinesView === 'all'
                    ? 'bg-white text-blue-700 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All Fines
              </button>
            </div>
            {canBulkRecall && (
              <button
                type="button"
                disabled={selectedFines.length === 0 || isRecallingFines}
                onClick={() => setShowBulkRecallModal(true)}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-md text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <RotateCcw className="h-4 w-4" />
                Bulk Recall ({selectedFines.length})
              </button>
            )}
          </div>
        )}
        {finesLoading ? (
          <TableSkeleton />
        ) : (
          <FinesTable
            data={localFines}
            onRowClick={handleRowClick}
            currentPage={currentPage}
            pageSize={pageSize}
            goToPage={goToPage}
            totalPages={totalPages}
            onRowUpdate={handleRowUpdate}
            isLoading={isPending}
            onFilter={handleFilter}
            totalEntries={totalEntries}
            onSend={handleSend}
            onSendingSms={isNotifyPending}
            onPageSizeChange={handlePageSizeChange} // 👈 new
            selectable={canBulkRecall}
            selectedFines={selectedFines}
            onToggleFineSelection={handleToggleFineSelection}
            onToggleSelectAllFines={handleToggleSelectAllFines}
          />
        )}
      </div>
    </div>
  );
};

export default Fines;
